import { useSignIn, useSignUp } from '@clerk/react'
import { useState, type SyntheticEvent } from 'react'
import { Boton } from '@/components/ui/Boton'

/**
 * Ingreso en pruebas (§1.4 del plan): modo test de Clerk, sólo fuera de
 * producción. Mails `nombre+clerk_test@ejemplo.com` con el código fijo
 * 424242, sin pasar por Google. En producción este componente no se
 * renderiza y el build lo omite (ver `modoTestHabilitado`).
 *
 * Si el mail no existe todavía en Clerk, se registra en el mismo paso.
 */
export const CODIGO_DE_PRUEBA = '424242'

export function IngresoDePrueba() {
  const { signIn } = useSignIn()
  const { signUp } = useSignUp()

  const [abierto, setAbierto] = useState(false)
  const [email, setEmail] = useState('')
  const [codigo, setCodigo] = useState(CODIGO_DE_PRUEBA)
  const [error, setError] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState(false)

  const enviar = async (evento: SyntheticEvent<HTMLFormElement>) => {
    evento.preventDefault()
    setOcupado(true)
    setError(null)

    try {
      await ingresarConCodigo(email.trim(), codigo.trim())
      window.location.assign('/')
    } catch (e) {
      setError(mensajeDe(e))
      setOcupado(false)
    }
  }

  /** Ingreso por código; si el mail no existe, alta y verificación en el mismo paso. */
  async function ingresarConCodigo(identificador: string, code: string): Promise<void> {
    const envio = await signIn.emailCode.sendCode({ emailAddress: identificador })

    if (!envio.error) {
      const verificacion = await signIn.emailCode.verifyCode({ code })
      if (verificacion.error) throw verificacion.error
      const fin = await signIn.finalize()
      if (fin.error) throw fin.error
      return
    }

    if (!esMailDesconocido(envio.error)) throw envio.error

    const alta = await signUp.create({ emailAddress: identificador })
    if (alta.error) throw alta.error
    const enviado = await signUp.verifications.sendEmailCode()
    if (enviado.error) throw enviado.error
    const verificado = await signUp.verifications.verifyEmailCode({ code })
    if (verificado.error) throw verificado.error
    const fin = await signUp.finalize()
    if (fin.error) throw fin.error
  }

  if (!abierto) {
    return (
      <p className="mt-4 text-center">
        <button
          type="button"
          className="text-sm text-tinta-suave underline underline-offset-4 hover:text-tinta"
          onClick={() => {
            setAbierto(true)
          }}
        >
          Ingreso de prueba
        </button>
      </p>
    )
  }

  return (
    <form
      onSubmit={(e) => void enviar(e)}
      className="mt-6 rounded-2xl border border-dashed border-borde p-4"
      aria-label="Ingreso de prueba"
    >
      <p className="text-xs text-tinta-suave">
        Sólo fuera de producción. Mail con <code>+clerk_test</code> y el código fijo.
      </p>
      <label className="mt-3 block text-sm">
        <span className="text-tinta-suave">Mail de prueba</span>
        <input
          type="email"
          name="email"
          required
          autoComplete="off"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value)
          }}
          placeholder="valentina+clerk_test@docba.com.ar"
          className="mt-1 w-full rounded-xl border border-borde bg-tarjeta px-3 py-2.5 text-base sm:text-sm"
        />
      </label>
      <label className="mt-3 block text-sm">
        <span className="text-tinta-suave">Código</span>
        <input
          type="text"
          name="codigo"
          inputMode="numeric"
          required
          value={codigo}
          onChange={(e) => {
            setCodigo(e.target.value)
          }}
          className="mt-1 w-full rounded-xl border border-borde bg-tarjeta px-3 py-2.5 text-base sm:text-sm"
        />
      </label>
      {error && (
        <p role="alert" className="mt-3 text-sm text-nuevo">
          {error}
        </p>
      )}
      <Boton
        type="submit"
        variante="secundario"
        className="mt-4 w-full"
        disabled={ocupado}
      >
        Entrar en modo prueba
      </Boton>
    </form>
  )
}

function mensajeDe(e: unknown): string {
  if (
    e &&
    typeof e === 'object' &&
    'longMessage' in e &&
    typeof e.longMessage === 'string'
  ) {
    return e.longMessage
  }
  return e instanceof Error ? e.message : 'No se pudo entrar'
}

/**
 * El código de la API viene en `errors[0].code` (ClerkAPIResponseError); el
 * `code` de la envoltura es genérico. Se aceptan los dos por las dudas.
 */
function esMailDesconocido(e: unknown): boolean {
  const codigos = new Set<string>()
  if (e && typeof e === 'object') {
    const err = e as { code?: string; errors?: { code?: string }[] }
    if (err.code) codigos.add(err.code)
    for (const x of err.errors ?? []) if (x.code) codigos.add(x.code)
  }
  return codigos.has('form_identifier_not_found')
}
